/* eslint-disable no-magic-numbers -- 3D navigation math and rendering tolerances */
// Copies of small @technology/web3d helpers that are not part of its public
// entry point. Deep `dist/esnext` imports would bundle a second web3d/three
// instance in apps that load web3d from a CDN (tcweb).
import {
  Mesh,
  Object3D,
  Raycaster,
  Vector2,
  Vector3,
  type IIntersection,
  type Model,
  type MouseButton,
  type ProjectionType,
  type SnapType,
  type Web3DEventName,
} from '@technology/web3d';
import type { Api } from '@technology/web3d/dist/esnext/Api.js';
import type { PointerInput } from '@technology/web3d/dist/esnext/InputHandler.js';
import {
  defer,
  interval,
  map,
  type Observable,
  type SchedulerLike,
} from 'rxjs';

// web3d declares these as ambient `const enum`s, which are erased from its
// bundle and cannot be read at runtime under isolatedModules.
export const MouseButtonValue = {
  left: 0 as MouseButton,
  middle: 1 as MouseButton,
  right: 2 as MouseButton,
} as const;
export const SnapTypeValue = {
  FACE: 0 as SnapType,
  LINE: 1 as SnapType,
  POINT: 2 as SnapType,
} as const;
/** The template parameter type only accepts values of the string enum `E`. */
const stringEnumValue = <E extends string>(value: `${E}`): E => value as E;

export const ProjectionTypeValue = {
  ortho: stringEnumValue<ProjectionType>('ortho'),
  perspective: stringEnumValue<ProjectionType>('perspective'),
} as const;
export const Web3DEventNameValue = {
  modelLoad: stringEnumValue<Web3DEventName>('modelload'),
  modelUnload: stringEnumValue<Web3DEventName>('modelunload'),
} as const;

export class Vector3Const {
  public static readonly zero = Object.freeze(new Vector3(0, 0, 0));
  public static readonly up = Object.freeze(new Vector3(0, 0, 1));
  public static readonly down = Object.freeze(new Vector3(0, 0, -1));
  public static readonly forward = Object.freeze(new Vector3(0, 1, 0));
  public static readonly right = Object.freeze(new Vector3(1, 0, 0));
  // For historical reasons threejs camera is looking back
  public static readonly threejsCameraForward = Object.freeze(
    new Vector3(0, 0, -1)
  );
}

export const msElapsed = (scheduler: SchedulerLike): Observable<number> =>
  defer(() => {
    const start = scheduler.now();
    return interval(0, scheduler).pipe(map(() => scheduler.now() - start));
  });

export const addIntersection =
  (picker: Api['picker'], models?: Model[]) =>
  async (e: PointerInput): Promise<PointerInput> => {
    e.intersection = await picker.pick(new Vector2(e.x, e.y), models);
    return e;
  };

export function createCaster(): IIntersection['caster'] {
  return new Raycaster() as IIntersection['caster'];
}

function calculateSphericalYAngle(up: Vector3): number {
  if (up.x === 0.0) {
    return up.y > 0 ? Math.PI / 2 : -Math.PI / 2;
  }
  if (up.y === 0) {
    return up.x > 0 ? 0.0 : Math.PI;
  }
  const angle = Math.atan2(Math.abs(up.y), Math.abs(up.x));
  if (up.x < 0) {
    return up.y < 0 ? angle + Math.PI : angle + Math.PI / 2;
  }
  return up.y < 0 ? angle + 1.5 * Math.PI : angle;
}

export function directionToSpherical(
  direction: Vector3,
  up: Vector3,
  target: Vector2 = new Vector2()
): Vector2 {
  let y: number;
  if (Math.abs(direction.x) < 0.002 && Math.abs(direction.y) < 0.002) {
    y =
      direction.z > 0
        ? Math.PI + calculateSphericalYAngle(up)
        : calculateSphericalYAngle(up);
  } else {
    y = Math.atan2(direction.y, direction.x);
  }
  return target.set(Math.acos(direction.z), y);
}

const objectWorldPosition = new Vector3();
const cameraWorldPosition = new Vector3();

export function makeScreenStaticSize(object: Object3D, api: Api): void {
  object.renderOrder = 1;
  let mesh: Mesh | undefined;
  object.traverse((o) => {
    if (o instanceof Mesh) mesh = o;
  });
  if (mesh) {
    mesh.onBeforeRender = (): void => {
      const scale = api.camera.getProjectionCompensatingScale(
        object
          .getWorldPosition(objectWorldPosition)
          .distanceTo(api.camera.getWorldPosition(cameraWorldPosition))
      );
      object.scale.set(scale, scale, scale);
      object.updateMatrixWorld(true);
    };
  }
}
